/**
 * UiHeroScrollFxController — Waabi mainScrollAnimation 포팅.
 * 정본: workspace/main-animation/script.js
 *
 * 본 프로젝트 환경 차이점:
 *   1. new Lenis() / lenis.on / gsap.ticker.add(lenis.raf) 직접 호출 제거
 *      → SmoothScrollManager 가 lenis ↔ ScrollTrigger 동기화 일임
 *   2. SplitText.create → split-type 패키지 new SplitType (Waabi SplitText 유료)
 *   3. 셀렉터 prefix: .hero → .sec-ui-hero-scroll-fx__hero
 *   4. aboutImgs 패럴렉스: 자체 ScrollTrigger 미사용.
 *      hero pin(pinSpacing:false) + about(margin-top:275svh) 조합에서 about element 의
 *      rect 측정이 깨져 progress 가 1 로 고정되는 현상 → about 트리거를 만들지 않고
 *      hero ST onUpdate 안에서 hero progress 0.5~1.0 구간을 8장 이미지의 (x,y) 보간에
 *      매핑. 각 이미지는 SCSS 절대좌표(최종 위치) + GSAP transform(시작 offset → 0) 합성.
 *   5. 모바일/데스크탑 동일 등록 (Waabi 원본 정책). viewport 별 yScale 로 점프 거리만
 *      조정 (좁은 viewport 에서 큰 점프 회피).
 *
 * prefers-reduced-motion 시 등록 스킵.
 */

import SplitType from "split-type";

import { getGsap, getScrollTrigger } from "../global/GsapGlobals";
import { DUR, EASE, STAGGER, motionOK } from "../global/MotionTokens";

const ROOT_SEL = ".sec-ui-hero-scroll-fx";

export const UiHeroScrollFxController = {
    init(): void {
        const root = document.querySelector<HTMLElement>(ROOT_SEL);
        if (!root) return;
        if (!motionOK()) return;

        const gsap = getGsap();
        const ScrollTrigger = getScrollTrigger();

        // ── Hero 헤드라인 진입(로드) 애니메이션 ──────────────────
        // h1 을 lines+words 로 분해 → 로드 직후 줄별 마스크 안에서 word 가
        // 아래→위로 차분히 솟아오름 (브랜드 톤: DUR.epic · STAGGER.base · expo.out).
        // 초기 숨김은 SCSS `.is-intro-ready .word` 가 전담(JS set 아님). 마커는 JS 가
        // split 직후 부착 → JS 미동작 시 원문 노출(접근성 안전).
        // 부제(__hero-copy)와 독립이어야 하므로 heroCopyEl 가드보다 위에 둔다.
        const headlineEl = root.querySelector<HTMLElement>(
            `${ROOT_SEL}__hero-header h1`,
        );
        let introTween: gsap.core.Tween | null = null;

        if (headlineEl) {
            const headlineSplit = new SplitType(headlineEl, {
                types: "lines,words",
                tagName: "span",
            });
            const headlineWords = (headlineSplit.words ?? []) as HTMLElement[];

            if (headlineWords.length) {
                // 마커 부착 → 이 순간부터 SCSS 가 .word 숨김 (동기 → FOUC 없음)
                headlineEl.classList.add("is-intro-ready");
                // 부모 h1 은 SCSS @media(scripting:enabled) 로 opacity:0 → reveal 책임을
                // word 로 이양하기 위해 부모만 노출 복귀. (word 는 .is-intro-ready 로 0 유지)
                gsap.set(headlineEl, { opacity: 1 });
                // SCSS translateY(110%) 를 gsap yPercent 로 동기화.
                // gsap 은 CSS raw transform 을 yPercent 시작값으로 읽지 못해 0→0 무변화 tween
                // 이 되므로, 명시적으로 yPercent:110 을 인라인에 못박은 뒤 0 으로 tween.
                gsap.set(headlineWords, { yPercent: 110 });

                const startIntro = () => {
                    introTween = gsap.to(headlineWords, {
                        yPercent: 0,
                        opacity: 1,
                        duration: DUR.epic, // 1.2s
                        ease: EASE.outExpo, // expo.out
                        stagger: STAGGER.base, // 0.06s
                        onComplete: () => {
                            gsap.set(headlineWords, { willChange: "auto" });
                        },
                    });
                };
                // 숨김 상태가 최소 1프레임 페인트된 뒤 reveal 시작 (등장 누락 방지)
                requestAnimationFrame(() =>
                    requestAnimationFrame(startIntro),
                );
            }
        }

        const heroCopyEl = root.querySelector<HTMLElement>(
            `${ROOT_SEL}__hero-copy h3`,
        );
        if (!heroCopyEl) return;

        const heroCopySplit = new SplitType(heroCopyEl, {
            types: "words",
            tagName: "span",
        });
        const words = (heroCopySplit.words ?? []) as HTMLElement[];

        // 부제 FOUC 차단: word 인라인 0 선점 후 부모 h3 노출 복귀(SCSS opacity:0 해제).
        // 첫 onUpdate(progress≈0)에서 word opacity=0 그대로라 깜빡임 없이 이어진다.
        gsap.set(words, { opacity: 0 });
        gsap.set(heroCopyEl, { opacity: 1 });

        const isDesktop = window.matchMedia("(min-width: 1367px)").matches;
        const isTabletOrLaptop = window.matchMedia(
            "(min-width: 1001px) and (max-width: 1366px)",
        ).matches;

        // viewport 별 점프 거리 스케일 — 외측(±900)/내측(±600) 기존 1000/500 와 동급
        let yScale = 1;
        if (isTabletOrLaptop) yScale = 0.7;
        else if (!isDesktop) yScale = 0.5;

        // 8장 이미지 시작 offset (px). y 음수 = 위에서 내려옴, 양수 = 아래에서 올라옴.
        // 외측(1·4·6·8)±900, 내측(2·3·5·7)±600 으로 깊이감. x 미세 좌우 비대칭.
        const imgMotion: Array<{ id: string; y: number; x: number }> = [
            { id: "#about-img-1", y: -900 * yScale, x: -40 * yScale },
            { id: "#about-img-2", y: -600 * yScale, x: -20 * yScale },
            { id: "#about-img-3", y: -600 * yScale, x:  20 * yScale },
            { id: "#about-img-4", y: -900 * yScale, x:  40 * yScale },
            { id: "#about-img-5", y:  600 * yScale, x: -20 * yScale },
            { id: "#about-img-6", y:  900 * yScale, x: -40 * yScale },
            { id: "#about-img-7", y:  600 * yScale, x:  20 * yScale },
            { id: "#about-img-8", y:  900 * yScale, x:  40 * yScale },
        ];

        let isHeroCopyHidden = false;

        ScrollTrigger.create({
            trigger: `${ROOT_SEL}__hero`,
            start: "top top",
            end: `+=${window.innerHeight * 3.5}`,
            pin: true,
            pinSpacing: false,
            scrub: 1,
            onUpdate: (self) => {
                const progress = self.progress;

                // 스크롤 시작 즉시 진입 트윈을 최종 상태로 스냅 (시간적 겹침 회피)
                if (progress > 0 && introTween && introTween.isActive()) {
                    introTween.progress(1);
                }

                const heroHeaderProgress = Math.min(progress / 0.29, 1);
                gsap.set(`${ROOT_SEL}__hero-header`, {
                    yPercent: -heroHeaderProgress * 100,
                });

                const heroWordsProgress = Math.max(
                    0,
                    Math.min((progress - 0.29) / 0.21, 1),
                );

                const totalWords = words.length;

                words.forEach((word, i) => {
                    const wordStart = i / totalWords;
                    const wordEnd = (i + 1) / totalWords;

                    const wordOpacity = Math.max(
                        0,
                        Math.min(
                            (heroWordsProgress - wordStart) /
                                (wordEnd - wordStart),
                            1,
                        ),
                    );

                    gsap.set(word, {
                        opacity: wordOpacity,
                    });
                });

                if (progress > 0.64 && !isHeroCopyHidden) {
                    isHeroCopyHidden = true;

                    gsap.to(`${ROOT_SEL}__hero-copy h3`, {
                        opacity: 0,
                        duration: 0.2,
                    });
                } else if (progress <= 0.64 && isHeroCopyHidden) {
                    isHeroCopyHidden = false;

                    gsap.to(`${ROOT_SEL}__hero-copy h3`, {
                        opacity: 1,
                        duration: 0.2,
                    });
                }

                const heroImgProgress = Math.max(
                    0,
                    Math.min((progress - 0.71) / 0.29, 1),
                );

                const heroImgWidth = gsap.utils.interpolate(
                    window.innerWidth,
                    150,
                    heroImgProgress,
                );

                const heroImgHeight = gsap.utils.interpolate(
                    window.innerHeight,
                    150,
                    heroImgProgress,
                );

                const heroImgBorderRadius = gsap.utils.interpolate(
                    0,
                    10,
                    heroImgProgress,
                );

                gsap.set(`${ROOT_SEL}__hero-img`, {
                    width: heroImgWidth,
                    height: heroImgHeight,
                    borderRadius: heroImgBorderRadius,
                });

                // about 헤더 카피 페이드인 — 이미지 패럴렉스 수렴 후반(0.85~1.0)에 떠오름.
                // 초기 숨김은 SCSS @media(scripting:enabled), reveal 은 여기 보간이 담당.
                const aboutHeaderProgress = Math.max(
                    0,
                    Math.min((progress - 0.85) / 0.15, 1),
                );
                gsap.set(`${ROOT_SEL}__about-header h3`, {
                    opacity: aboutHeaderProgress,
                });

                // about 이미지 패럴렉스 — hero progress 에 종속.
                // 자체 ST 미사용 (pinSpacing:false 측정 문제 회피).
                // 8장이 각자 시작 (x,y) 에서 (0,0) 으로 수렴 → SCSS 절대좌표가 최종 위치.
                const aboutColsProgress = Math.max(
                    0,
                    Math.min((progress - 0.5) / 0.5, 1),
                );
                imgMotion.forEach(({ id, y, x }) => {
                    gsap.set(id, {
                        x: gsap.utils.interpolate(x, 0, aboutColsProgress),
                        y: gsap.utils.interpolate(y, 0, aboutColsProgress),
                    });
                });
            },
        });

        if (document.readyState === "complete") {
            ScrollTrigger.refresh();
        } else {
            window.addEventListener(
                "load",
                () => ScrollTrigger.refresh(),
                { once: true },
            );
        }
    },
};
